import { ref, onUnmounted } from 'vue';
import { socketService } from '@/services/socket.service';
import { CHANNELS, POS_BRANCH_EVENTS } from '@/services/socket.events';

/**
 * Branch-wide POS socket — subscribes to private-pos.{branchId}.
 * Use on staff overview pages (/pos/table, /pos/sessions) so staff receive
 * notifications for EVERY table in the branch regardless of which sub-page
 * they are on.
 *
 * For a single dining session's item/order state use usePOSSessionSocket.
 *
 * @param {object}   options
 * @param {number}   options.branchId
 * @param {Function} [options.onRequested]  (data) => void   customer pressed "call staff"
 * @param {Function} [options.onBillRequested]  (data) => void   customer asked to check bill
 */
export function usePOSBranchSocket({ branchId, onRequested, onBillRequested, onOpen, onClose }) {
  const channel = CHANNELS.posBranch(branchId);

  const isConnected = ref(socketService.isConnected());

  // ─── Track connection state ───────────────────────────────────────────────
  const offChange = socketService.onConnectionChange((isUp) => { isConnected.value = isUp; });

  // ─── Subscribe to private channel ────────────────────────────────────────
  socketService.subscribe(channel);

  const offRequested = socketService.on(channel, POS_BRANCH_EVENTS.REQUESTED, (data) => {
    onRequested?.(data);
  });

  const offBillRequested = socketService.on(channel, POS_BRANCH_EVENTS.BILL_REQUESTED, (data) => {
    onBillRequested?.(data);
  });

  const offOpen = socketService.on(channel, POS_BRANCH_EVENTS.OPEN_SESSION, (data) => {
    onOpen?.(data);
  });

  const offClose = socketService.on(channel, POS_BRANCH_EVENTS.CLOSE_SESSION, (data) => {
    onClose?.(data);
  });

  // ─── Cleanup ──────────────────────────────────────────────────────────────
  onUnmounted(() => {
    offChange();
    offRequested();
    offBillRequested();
    offOpen();
    offClose();
    socketService.unsubscribe(channel);
  });

  return {
    isConnected,
  };
}
